/**
 * Application constants for group portal.
 *
 * Centralized constants for session configuration, validation rules,
 * authentication steps, and other application-wide values. This eliminates
 * magic numbers/strings and provides a single source of truth.
 */

/**
 * WorkOS role assigned to internal Enrollment Admin staff (EOM-441/EOM-473).
 * Distinct from the 'enrollment' roleSlug (an external group-portal member) --
 * this is an unscoped superuser role with no Salesforce Contact/ACR. Checked
 * against session.role wherever the enrollmentAdminBypass flag applies.
 *
 * Must be underscore, not hyphen: the backend's role-to-authority mapping
 * computes `"ROLE_" + role.toUpperCase()` and requires the result to equal
 * `RoleType.ROLE_ENROLLMENT_ADMIN` -- a hyphen here would produce
 * `ROLE_ENROLLMENT-ADMIN`, which never matches. This value must match the
 * WorkOS org membership's role slug exactly (set manually in the WorkOS
 * Dashboard per EOM-619, not assigned by this app's code -- see `roleSlugFor`
 * in `workos-organizations.ts`, which is otherwise dead for this kind).
 */
export const ROLE_ENROLLMENT_ADMIN = 'enrollment_admin'

export const SESSION_CONFIG = {
  /** Idle timeout in seconds (30 minutes for HIPAA compliance) */
  IDLE_TIMEOUT_SECONDS: 30 * 60,
  /**
   * Absolute session cap in seconds (7 days from login). Activity and token
   * refreshes do not extend it.
   */
  MAX_AGE_SECONDS: 7 * 24 * 60 * 60,
  /** Show warning modal this many minutes before idle timeout (2 minutes) */
  IDLE_WARNING_MINUTES: 2,
  /**
   * How long a cached Salesforce access snapshot (groupData: groupUuids +
   * access flags) is trusted before it must be re-resolved from ACRs. Bounds
   * staleness for both newly-granted access (becomes visible within this window
   * without a full re-login) and revoked access (enforced within this window
   * mid-session). 15 minutes.
   */
  GROUP_DATA_TTL_SECONDS: 15 * 60,
} as const

export const VALIDATION_RULES = {
  PASSWORD_MIN_LENGTH: 8,
  OTP_CODE_LENGTH: 6,
  /** Cooldown period for resending verification code (seconds) */
  RESEND_COOLDOWN_SECONDS: 60,
  EMAIL_REGEX: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
} as const

/**
 * Password requirements for registration and password reset flows. Matches
 * WorkOS password requirements.
 */
/**
 * The one place the accepted symbol set is written down. The regex, the
 * checklist label and the Yup error message are all derived from it below, so
 * adding or removing a character here updates every surface at once.
 *
 * NOTE: `lib/utils/password-generator.ts` deliberately does NOT use this. Its
 * output goes straight to WorkOS with no retry path, and the `_ . -` additions
 * were never verified against WorkOS's own accepted set, so it stays pinned to
 * the original 8 characters. Read that file's comment before widening this.
 */
const ALLOWED_SYMBOLS = '!@#$%^&*_.-'

/**
 * Escape a literal string for safe use inside a regex character class. Only
 * `\`, `]`, `^` and `-` can change a class's meaning; escaping them keeps a
 * derived pattern correct no matter what order ALLOWED_SYMBOLS is written in.
 */
const escapeForCharClass = (chars: string): string =>
  chars.replace(/[\\\]^-]/g, '\\$&')

const SYMBOL_PATTERN = new RegExp(`[${escapeForCharClass(ALLOWED_SYMBOLS)}]`)

/**
 * Rendered symbol list. Used by BOTH the checklist badge and the Yup error
 * message so the two surfaces cannot describe the same rule differently.
 */
const SYMBOL_LIST_DISPLAY = ALLOWED_SYMBOLS.split('').join(' ')

export const PASSWORD_REQUIREMENTS = {
  /** Minimum password length */
  MIN_LENGTH: 8,
  /** Allowed special characters */
  ALLOWED_SYMBOLS,
  /** Allowed special characters, space-separated for display */
  SYMBOL_LIST_DISPLAY,
  /** Regex patterns for individual password requirements */
  PATTERNS: {
    LOWERCASE: /[a-z]/,
    UPPERCASE: /[A-Z]/,
    NUMBER: /\d/,
    SYMBOL: SYMBOL_PATTERN,
  },
  /** Human-readable requirement descriptions */
  DESCRIPTIONS: {
    LOWERCASE: 'One lowercase character',
    UPPERCASE: 'One uppercase character',
    NUMBER: 'One number',
    SYMBOL: `One symbol: ${SYMBOL_LIST_DISPLAY}`,
    MIN_LENGTH: 'At least 8 characters',
  },
} as const

export const ROUTES = {
  HOME: '/',
  COMPANIES: '/companies',
  QUOTES: '/quotes',
  REPORTS: '/reports',
  EMPLOYEES: '/employees',
  DOCUMENTS: '/documents',
  ENROLLMENT_ERRORS: '/enrollment-errors',
  SETTINGS: '/settings',
  SETTINGS_ORGANIZATION: '/settings/organization',
  SETTINGS_CONTRACTS: '/settings/contracts',
  EMPLOYEES_ADD: '/employees/add',
  EMPLOYEES_UPLOAD: '/employees/upload',
  // Billing
  BILLING: '/billing',
  // Auth routes
  LOGIN: '/login',
  REGISTER: '/register',
  REGISTER_CHECK_EMAIL: '/register/check-email',
  SET_PASSWORD: '/register/set-password',
  FORGOT_PASSWORD: '/forgot-password',
  FORGOT_PASSWORD_CHECK_EMAIL: '/forgot-password/check-email',
  RESET_PASSWORD: '/forgot-password/reset-password',
} as const

/**
 * Companies list with the "not available yet" notice, the target when a guard
 * refuses a group with mode `none`.
 */
export const COMPANIES_UNAVAILABLE_NOTICE = 'unavailable'
export const COMPANIES_UNAVAILABLE_HREF = `${ROUTES.COMPANIES}?notice=${COMPANIES_UNAVAILABLE_NOTICE}`

/**
 * Subpaths under a `/companies/[groupId]` route, used by `groupScopedRoute` to
 * build group-scoped URLs on the multi-company path. Single source of truth so
 * nav, the cog picker, and internal navigation cannot drift from the route
 * folder names.
 */
export const COMPANY_SUBROUTES = {
  RENEWALS: 'renewals',
  ROSTER: 'roster',
  SETTINGS: 'settings',
  SETTINGS_CONTRACTS: 'settings/contracts',
  DOCUMENTS: 'documents',
} as const

export const API_ENDPOINTS = {
  HEALTH: '/api/health',
  LOGOUT: '/api/logout',
} as const

/**
 * User-facing messages for the bulk CSV upload flow.
 *
 * SESSION_EXPIRED is shown only when the upload result carries errorCode:
 * 'AUTH_ERROR', meaning the proxy bounced an expired or unauthorized session.
 * UPLOAD_FAILED is the generic infrastructure-failure message shown when
 * submitBulkUpload throws (network drop, RSC transport error, JSON parse of an
 * HTML login redirect). These are all transient transport failures, so the copy
 * asks the user to retry, not to refresh the page (refreshing does nothing for
 * the underlying failure and just loses their place).
 */
export const BULK_UPLOAD_MESSAGES = {
  SESSION_EXPIRED: 'Session expired. Please log in again.',
  UPLOAD_FAILED:
    'Something went wrong reaching the server, so your file was not uploaded. Please try again in a moment.',
} as const

/**
 * Default page size for server-paginated member list requests. The client-side
 * member list will fetch this many rows initially and paginate through
 * subsequent pages on demand (EOM-369 Phase C).
 */
export const DEFAULT_PAGE_SIZE = 25

/**
 * Count cap on the single-value broker claim, used only while the
 * `shardedClaimWriteEnabled` flag is off (EOM-475). With the flag on, the byte
 * budget in `broker-claim-shards.ts` replaces it (EOM-1253). A broker whose
 * real ACR group count exceeds the limit gets a truncated claim plus a
 * structured log line (never a silent drop, never a login failure).
 */
export const LEGACY_MAX_BROKER_GROUP_UUIDS = 50

/**
 * HouseholdCoverage.status values the FE branches on. The field itself is an
 * open string enum on the backend (`status: string`); only the value used for
 * plan-change display logic is named here, so it can't drift between files.
 */
export const HOUSEHOLD_COVERAGE_STATUS = {
  ACTIVE: 'ACTIVE',
  PENDING_ACTIVATION: 'PENDING_ACTIVATION',
} as const

/**
 * Shown when a renewal submission is refused because the group's enrollment is
 * also managed by the EDI full-file feed (EOM-525). Lives here, not in
 * `lib/services/renewals.service.ts`, because that module carries `'use
 * server'`, which restricts it to async function exports; a client-rendered
 * notice component needs to import this same string, so it cannot live in a
 * server-actions-only module. Keep this the single source so the Server
 * Action's refusal and the notice component's copy cannot drift apart.
 */
export const EDI_MANAGED_MESSAGE =
  'This group’s enrollment is managed by a full-file feed. Renewals for it cannot be submitted from the portal.'

/** Persistent roster notice for an EDI-managed group in read-only mode. */
export const EDI_READONLY_ROSTER_MESSAGE =
  'Enrollment for this group is managed through the EDI feed. Contact your account manager to make changes.'

/** Returned by a write Server Action refused because the group is read-only. */
export const EDI_READONLY_WRITE_ERROR =
  'Enrollment for this group is managed through the EDI feed and cannot be changed here.'

/** Returned by a write Server Action refused because the group is `none` mode. */
export const GROUP_NO_ACCESS_ERROR = 'You do not have access to this company.'

/**
 * LaunchDarkly JSON flag deciding which EDI-managed groups get the read-only
 * roster experience. Value shape: `{ "allowAll": boolean, "groupUuids":
 * string[] }`. Temporary: remove the flag and its check after the all-EDI
 * rollout completes.
 */
export const EDI_READONLY_FLAG_KEY = 'group-edi-readonly-access'

/**
 * Largest name+value, in bytes, of one session cookie. Browsers silently refuse
 * a Set-Cookie above their limit. RFC 6265 section 6.1 only promises "at least
 * 4096 bytes per cookie" counting name, value AND attributes, and we could not
 * confirm Safari's exact cutoff from documentation, so 3800 leaves about 300
 * bytes for the attributes we send (about 70) and for any browser that counts
 * differently.
 */
export const SESSION_COOKIE_MAX_BYTES = 3800
/**
 * Most cookies one session may span, so at most 3 x 3800 = 11,400 name+value
 * bytes of the Cookie request header (Node's default total header limit is 16
 * KiB, and AWS load balancers cap a single header at 16 K); this is where login
 * fails with a clear error instead of a silently dropped session. The last
 * chunk in use also logs a warning, so growth is visible before logins start
 * failing.
 */
export const SESSION_COOKIE_MAX_CHUNKS = 3
